import { useContext, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { MyContext } from '../../../context';
import WorkflowRunTimeline from '../../../components/WorkflowRunTimeline';
import { getWorkflowRun, retryWorkflowRun, resumeWorkflowRun, cancelWorkflowRun } from '../../../utils/queries';

export default function WorkflowRunDetail() {
	const { id } = useParams();
	const { activeStoreId } = useContext(MyContext);
	const [run, setRun] = useState(null);
	const [loading, setLoading] = useState(true);
	const [working, setWorking] = useState(false);

	const refresh = () => {
		setLoading(true);
		getWorkflowRun(id, { storeId: activeStoreId }).then((result) => {
			if (result?.success) setRun(result.data);
			setLoading(false);
		});
	};

	useEffect(() => {
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id, activeStoreId]);

	const runAction = async (fn, successMessage) => {
		setWorking(true);
		const result = await fn(id, activeStoreId);
		setWorking(false);
		if (!result?.success) {
			toast.error(result?.error?.message || 'Action failed.');
			return;
		}
		toast.success(successMessage);
		refresh();
	};

	if (loading) return <div className='py-8 pr-8 text-sm text-gray-400'>Loading...</div>;
	if (!run) return <div className='py-8 pr-8 text-sm text-gray-500'>Workflow run not found.</div>;

	return (
		<div className='py-8 pr-8 max-w-3xl'>
			<Link to='/workflows/runs' className='text-sm text-gray-500 hover:underline'>
				&larr; All Runs
			</Link>
			<div className='flex items-center justify-between mt-1 mb-6'>
				<div>
					<h1 className='text-2xl font-light tracking-tight'>{run.workflow_name || `Workflow #${run.workflow_definition_id}`}</h1>
					<p className='text-sm text-gray-500'>
						{run.entity_type} #{run.entity_id} &middot; {run.status}
						{run.current_step_id ? ` · at ${run.current_step_id}` : ''}
					</p>
				</div>
				<div className='flex gap-2'>
					{(run.status === 'FAILED' || run.status === 'WAITING') && (
						<button type='button' className='btn-secondary-small' disabled={working} onClick={() => runAction(retryWorkflowRun, 'Retrying...')}>
							Retry
						</button>
					)}
					{run.status === 'WAITING' && (
						<button type='button' className='btn-secondary-small' disabled={working} onClick={() => runAction(resumeWorkflowRun, 'Resumed.')}>
							Resume
						</button>
					)}
					{['PENDING', 'RUNNING', 'WAITING'].includes(run.status) && (
						<button type='button' className='btn-secondary-small' disabled={working} onClick={() => runAction(cancelWorkflowRun, 'Cancelled.')}>
							Cancel
						</button>
					)}
				</div>
			</div>

			<h2 className='font-medium mb-3'>Timeline</h2>
			<WorkflowRunTimeline steps={run.steps} />

			{run.approvals?.length > 0 && (
				<div className='mt-8'>
					<h2 className='font-medium mb-3'>Approvals</h2>
					<div className='space-y-2'>
						{run.approvals.map((approval) => (
							<div key={approval.workflowApprovalId} className='border border-gray-200 rounded-md px-3 py-2 text-sm flex items-center justify-between'>
								<span>Step {approval.stepId}</span>
								<span className='text-gray-500'>{approval.status}</span>
							</div>
						))}
					</div>
				</div>
			)}

			{run.exceptions?.length > 0 && (
				<div className='mt-8'>
					<h2 className='font-medium mb-3'>Exceptions</h2>
					<div className='space-y-2'>
						{run.exceptions.map((exception) => (
							<div key={exception.workflowExceptionId} className='border border-red-200 bg-red-50 rounded-md px-3 py-2 text-sm'>
								<div className='flex items-center justify-between'>
									<span>Step {exception.stepId}</span>
									<span className='text-red-700'>{exception.status}</span>
								</div>
								{exception.errorMessage && <p className='text-xs text-gray-500 mt-1'>{exception.errorMessage}</p>}
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
}
